/**
 * SCK-705 — post-NDA "Welcome Aboard" beta modal.
 *
 * Fires ONCE per user on the ManageApps landing page after they've
 * cleared the NDA gate. Distinct from GettingStartedModal (SCK-607,
 * variant-driven "next-step" bullets) — this modal is the beta
 * program orientation: what the beta is, how long it lasts, how to
 * give feedback, and a plain-language summary of the beta T&C
 * (full binding NDA was already accepted at the gate).
 *
 * Content source: `docs/beta-welcome-v0.md` (operator-provided
 * Clue2app-Beta Welcome-v0.docx, third variant — "Welcome Aboard").
 *
 * Persistence:
 *   • localStorage['welcome-modal-dismissed-v1:<jwt-sub>'] === 'true'
 *     → skip on mount. Scoped by JWT `sub` so two users on a shared
 *     browser don't share dismissal.
 *   • Written when the user clicks "Get started" OR closes the modal.
 *   • try/catch wrapped — private-mode failures fail-closed (we would
 *     rather flash on refresh than crash the landing page).
 *
 * Trigger gates (all must be true for the modal to open):
 *   1. A valid JWT is present (so we have a `sub` to key by).
 *   2. currentUser.userType !== 'SYSTEM' — SYSTEM operators don't
 *      go through the beta onboarding. Exception: a SYSTEM user can
 *      preview the modal by appending `?welcome=1` to any URL that
 *      renders <WelcomeModal /> (e.g. /apps?welcome=1). The override
 *      also bypasses the localStorage dismissal check so the modal
 *      renders unconditionally for QA / preview flows.
 *   3. `hasMembership` prop is true — caller has cleared the NDA
 *      + account-membership gate. ManageApps only renders once the
 *      user is past ProtectedRoutes + MainLayout, so today the caller
 *      passes `true` unconditionally; the prop is kept explicit so a
 *      future guard change can flip it without touching this file.
 *   4. No dismissal flag stored for this JWT sub (unless `?welcome=1`).
 *
 * Accessibility: MUI Dialog gives us focus trap + ESC-to-close by
 * default. We add `aria-labelledby` / `aria-describedby` and an
 * explicit close IconButton (screen-reader labeled).
 */

import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircleOutline,
  RocketLaunch as RocketIcon,
} from "@mui/icons-material";
import { jwtDecode } from "jwt-decode";

import { getToken } from "../helpers/tokenStorage";
import { USER_TYPE_SYSTEM } from "../helpers/constants";

export const WELCOME_STORAGE_KEY_PREFIX = "welcome-modal-dismissed-v1";

const storageKeyForSub = (sub) =>
  sub ? `${WELCOME_STORAGE_KEY_PREFIX}:${sub}` : null;

const safeRead = (key) => {
  if (!key) return null;
  try {
    return typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
};

const safeWrite = (key, val) => {
  if (!key) return;
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(key, val);
    }
  } catch {
    /* private mode / quota — silently ignored */
  }
};

// Exported for tests: clears the dismissal flag for a given sub.
export const clearWelcomeDismissed = (sub) => {
  const key = storageKeyForSub(sub);
  if (!key) return;
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
};

const decodeSub = (token) => {
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return decoded?.sub || null;
  } catch {
    return null;
  }
};

/**
 * Beta T&C summary bullets (numbered, from beta-welcome-v0). The
 * full binding NDA was already accepted at the /accept-nda gate —
 * this is a plain-language reminder, not a re-acceptance.
 */
const TC_ITEMS = [
  {
    title: "Beta License & Scope",
    body:
      "Limited, non-exclusive, non-transferable, revocable license for evaluation, testing, and feedback purposes during the 30-day Beta period.",
  },
  {
    title: "Experimental Nature & \"As-Is\" Provision",
    body:
      "Provided strictly \"AS IS\" and \"AS AVAILABLE\" without warranties.",
  },
  {
    title: "Data Usage & Confidentiality",
    body:
      "Do not upload sensitive personal data, proprietary source code, or critical production data. Feedback may be used freely. Platform features, UI design, and performance metrics are confidential.",
  },
  {
    title: "Intellectual Property & Restrictions on Use",
    body:
      "No copying, reverse-engineering, competing-model training, scraping, or sublicensing. Full IP remains Clue2Solve Inc.'s.",
  },
  {
    title: "Termination & Data Retention",
    body:
      "Access may be modified, suspended, or terminated at any time. Workspace data may be deleted after the 30-day period.",
  },
  {
    title: "Limitation of Liability",
    body:
      "Not liable for indirect, incidental, special, or consequential damages.",
  },
];

/**
 * Props:
 *   currentUser   — SharedProps `currentUser`; used to gate SYSTEM out.
 *   hasMembership — boolean, true once the caller has cleared the NDA
 *                   + account-membership gate. Defaults to true because
 *                   ManageApps only mounts inside ProtectedRoutes.
 *   ndaHref       — optional link target for the "Read the full NDA"
 *                   affordance. Defaults to the wiki NDA doc.
 */
export default function WelcomeModal({
  currentUser,
  hasMembership = true,
  ndaHref = "/legal/nda",
} = {}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const token = getToken();
  const sub = useMemo(() => decodeSub(token), [token]);
  const storageKey = useMemo(() => storageKeyForSub(sub), [sub]);

  const isSystem = currentUser?.userType === USER_TYPE_SYSTEM;

  // `?welcome=1` override — SYSTEM-only preview affordance so an
  // operator can render the modal without shipping it to every SYSTEM
  // user. Restricted to SYSTEM so a shared/bookmarked link carrying
  // ?welcome=1 cannot re-open the modal for regular users who already
  // dismissed it (would silently overwrite their dismissal state).
  // Computed once per mount; toggling the query string without reload
  // will not re-open the modal, which is fine for a manual-QA affordance.
  const queryFlag =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("welcome") === "1"
      : false;
  const forceShow = isSystem && queryFlag;

  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Fail-closed on every gate. We deliberately do NOT auto-open
    // when any of these is falsy — the modal is a nice-to-have, not
    // load-bearing UX.
    if (!token || !sub || !storageKey) return;
    if (isSystem && !forceShow) return;
    if (!hasMembership) return;
    if (safeRead(storageKey) === "true" && !forceShow) return;
    setOpen(true);
  }, [token, sub, storageKey, isSystem, hasMembership, forceShow]);

  const persistDismissed = () => {
    safeWrite(storageKey, "true");
  };

  const handleClose = () => {
    // Preview mode (?welcome=1) does NOT mutate the dismissal flag —
    // an operator previewing the modal shouldn't retroactively
    // "dismiss" it on their own account and then wonder why real users
    // (or their own future non-SYSTEM sessions) never see it.
    if (!forceShow) {
      persistDismissed();
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="welcome-modal-title"
      aria-describedby="welcome-modal-intro"
      data-testid="welcome-modal"
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: isDarkMode
            ? "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)"
            : "linear-gradient(180deg, #ffffff 0%, #fff7ed 100%)",
          color: isDarkMode ? "#e2e8f0" : "#0f172a",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          pr: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
          >
            Welcome to Clue2App
          </Typography>
          <Typography
            id="welcome-modal-title"
            variant="h5"
            sx={{
              fontWeight: 700,
              mt: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
            data-testid="welcome-modal-title"
          >
            Welcome Aboard!{" "}
            <RocketIcon
              fontSize="small"
              sx={{ color: "#EA580C" }}
              aria-hidden
            />
          </Typography>
        </Box>
        <IconButton
          aria-label="close welcome modal"
          onClick={handleClose}
          size="small"
          data-testid="welcome-modal-close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography
          id="welcome-modal-intro"
          variant="body1"
          sx={{ mb: 2, color: isDarkMode ? "#cbd5e1" : "#334155" }}
          data-testid="welcome-modal-intro"
        >
          We're thrilled to have you as an early explorer of our
          Agentic Intelligent Platform. Your real-world testing,
          insights, and feedback over the next 30 days are vital to
          shaping the future of our product.
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}
        >
          Welcome to the Beta Program
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 2, color: isDarkMode ? "#cbd5e1" : "#334155" }}
        >
          Get ready to test our next-generation agentic workflows. As
          a Beta user, you have exclusive access to try out features,
          push boundaries, and tell us what works — and what doesn't.
        </Typography>

        <List dense data-testid="welcome-modal-steps" sx={{ py: 0 }}>
          <ListItem disableGutters alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
              <CheckCircleOutline
                fontSize="small"
                sx={{ color: "#EA580C" }}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <span>
                  <strong>Goal:</strong> Use the platform, test your
                  daily workflows, and give us raw, actionable
                  feedback.
                </span>
              }
            />
          </ListItem>
          <ListItem disableGutters alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
              <CheckCircleOutline
                fontSize="small"
                sx={{ color: "#EA580C" }}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <span>
                  <strong>Duration:</strong> 30 days from your initial
                  login date.
                </span>
              }
            />
          </ListItem>
          <ListItem disableGutters alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
              <CheckCircleOutline
                fontSize="small"
                sx={{ color: "#EA580C" }}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <span>
                  <strong>Feedback Channels:</strong> Submit bugs,
                  feature requests, and general thoughts via the
                  in-app feedback button, or email us at{" "}
                  <Link
                    href="mailto:beta@clue2solve.com"
                    underline="hover"
                    sx={{ color: "#EA580C", fontWeight: 500 }}
                  >
                    beta@clue2solve.com
                  </Link>
                  .
                </span>
              }
            />
          </ListItem>
        </List>

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, mt: 3, mb: 0.5 }}
        >
          Beta Access Terms & Conditions
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 1.5, color: isDarkMode ? "#cbd5e1" : "#334155" }}
        >
          By accessing and using the Agentic Intelligent Platform
          ("Services"), you agree to the following temporary terms:
        </Typography>
        <Box
          component="ol"
          sx={{
            pl: 3,
            m: 0,
            "& li": { mb: 1 },
            color: isDarkMode ? "#cbd5e1" : "#334155",
          }}
          data-testid="welcome-modal-tc"
        >
          {TC_ITEMS.map((item) => (
            <li key={item.title}>
              <Typography variant="body2" component="span">
                <strong>{item.title}</strong> — {item.body}
              </Typography>
            </li>
          ))}
        </Box>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1.5,
            color: isDarkMode ? "#94a3b8" : "#64748b",
          }}
        >
          This is a summary. The full binding NDA you accepted at
          sign-in still applies —{" "}
          <Link
            href={ndaHref}
            underline="hover"
            sx={{ color: "#EA580C", fontWeight: 500 }}
            data-testid="welcome-modal-nda-link"
          >
            read the full NDA
          </Link>
          .
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end", gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            mr: "auto",
            color: isDarkMode ? "#cbd5e1" : "#334155",
            fontWeight: 500,
          }}
        >
          Ready to get started?
        </Typography>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            background: "#EA580C",
            "&:hover": { background: "#C2410C" },
          }}
          data-testid="welcome-modal-cta"
        >
          Get started
        </Button>
      </DialogActions>
    </Dialog>
  );
}
